import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { UI, primaryButtonSx, textButtonSx } from './settingsUi';
import { CategoryTag, TemplateStatusChip, WhatsappPhonePreview, contentHash, hasEdits } from './hubTemplates';
import type { TemplateStatus, TemplateStatusesApi } from './hubTemplates';
import { AUTO_REPLY_LANGS } from '../../utils/whatsappAutoReplyFlows';
import type { AutoReplyLang, LangText } from '../../utils/whatsappAutoReplyFlows';
import { TEMPLATE_DRAFTS, templateIssues, templatePreviewText } from '../../utils/whatsappTemplateDrafts';
import type { TemplateDraft } from '../../utils/whatsappTemplateDrafts';
import { JOURNEY_AUTOMATIONS } from '../../utils/whatsappJourney';

const BODY_MAX = 1024;
const FOOTER_MAX = 60;
const BUTTON_MAX = 25;

const STATUS_WORD: Record<string, string> = {
  APPROVED: 'Approved',
  PENDING: 'In review',
  IN_APPEAL: 'In appeal',
  REJECTED: 'Rejected',
  SUBMIT_FAILED: 'Submit failed',
  PAUSED: 'Paused by Meta',
  DISABLED: 'Disabled by Meta',
};

const statusWord = (s?: TemplateStatus) => (s ? STATUS_WORD[s.status] || s.status : 'Not submitted');

/** Variable checks: only known, sequential variables may be used. */
const variableIssues = (draft: TemplateDraft, lang: AutoReplyLang) => {
  const body = draft.body[lang];
  const used = new Set(Array.from(body.matchAll(/\{\{(\d+)\}\}/g)).map((m) => Number(m[1])));
  const issues: string[] = [];
  used.forEach((n) => {
    if (n < 1 || n > draft.variables.length) issues.push(`{{${n}}} is not a known variable. Ask for a new variable to be added first.`);
  });
  return issues;
};

const allIssues = (draft: TemplateDraft, lang: AutoReplyLang) => {
  const issues = [...variableIssues(draft, lang), ...templateIssues(draft, lang)];
  if (/\n{3,}/.test(draft.body[lang])) issues.push('Use at most one empty line between paragraphs.');
  return Array.from(new Set(issues.filter((i) => !i.startsWith('Every variable needs an example') && !i.startsWith('Variables must be numbered'))));
};

export const TemplateEditor = ({ base, api, onDone, initialLanguage = 'en' }: { base: TemplateDraft; api: TemplateStatusesApi; onDone: (text: string) => void; initialLanguage?: AutoReplyLang }) => {
  const saved = api.effective(base.name) || base;
  const [draft, setDraft] = useState<TemplateDraft>(saved);
  const [lang, setLang] = useState<AutoReplyLang>(initialLanguage);
  const [busy, setBusy] = useState<'save' | 'reset' | 'submit' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const savedHash = useMemo(() => JSON.stringify([saved.body, saved.footer, saved.buttons]), [saved]);
  const dirty = JSON.stringify([draft.body, draft.footer, draft.buttons]) !== savedHash;

  useEffect(() => {
    // Pick up a reset or a save from elsewhere when nothing is being edited here.
    if (!dirty) setDraft(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedHash]);

  const setBody = (value: string) => setDraft((d) => ({ ...d, body: { ...d.body, [lang]: value } }));
  const setFooter = (value: string) => setDraft((d) => ({ ...d, footer: d.footer ? { ...d.footer, [lang]: value } : d.footer }));
  const setButton = (index: number, value: string) =>
    setDraft((d) => ({ ...d, buttons: d.buttons?.map((b, i) => (i === index ? { ...b, [lang]: value } : b)) }));

  const insertVariable = (n: number) => {
    const token = `{{${n}}}`;
    const el = bodyRef.current;
    const text = draft.body[lang];
    if (!el) {
      setBody(`${text}${token}`);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    setBody(text.slice(0, start) + token + text.slice(end));
    window.requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const persist = async () => {
    const strip = (t: LangText, b: LangText): Partial<LangText> => ({ en: t.en.trim() === b.en ? '' : t.en, gu: t.gu.trim() === b.gu ? '' : t.gu, hi: t.hi.trim() === b.hi ? '' : t.hi });
    await api.saveEdits(base.name, {
      body: strip(draft.body, base.body),
      ...(draft.footer && base.footer ? { footer: strip(draft.footer, base.footer) } : {}),
      ...(draft.buttons && base.buttons ? { buttons: draft.buttons.map((b, i) => strip(b, base.buttons![i])) } : {}),
    });
  };

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      await persist();
      onDone('Saved. Submit to Meta when ready.');
    } catch (err: any) {
      setError(String(err?.message || err));
    } finally {
      setBusy(null);
    }
  };

  const reset = async () => {
    setBusy('reset');
    setError(null);
    try {
      await api.resetEdits(base.name);
      setDraft(base);
      onDone('Back to the original text (versions already sent to Meta are kept).');
    } catch (err: any) {
      setError(String(err?.message || err));
    } finally {
      setBusy(null);
    }
  };

  const submit = async (langs: AutoReplyLang[]) => {
    setError(null);
    for (const l of langs) {
      const issues = allIssues(draft, l);
      if (issues.length) {
        setLang(l);
        setError(issues.join(' '));
        return;
      }
    }
    setBusy('submit');
    try {
      if (dirty) await persist();
      for (const l of langs) {
        // eslint-disable-next-line no-await-in-loop
        const err = await api.submit(draft, l);
        if (err) {
          setLang(l);
          setError(err);
          return;
        }
      }
      onDone(`Submitted ${langs.map((l) => l.toUpperCase()).join(', ')} to Meta for review.`);
    } finally {
      setBusy(null);
    }
  };

  const issues = allIssues(draft, lang);
  const versions = api.versionsFor(base.name, lang);
  const pendingLangs = AUTO_REPLY_LANGS.map((l) => l.key).filter((l) => api.needsSubmit(base.name, l) || (dirty && contentHash(draft, l) !== contentHash(saved, l)));
  const fieldSx = { '& .MuiInputBase-root': { fontSize: 13, bgcolor: UI.surface } };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' }, gap: 2.5, px: 2, pb: 2, pt: 0.5 }}>
      <Stack gap={1.25} sx={{ minWidth: 0 }}>
        <Stack direction="row" gap={0.5}>
          {AUTO_REPLY_LANGS.map(({ key, label }) => (
            <Box
              key={key}
              component="button"
              type="button"
              onClick={() => setLang(key)}
              sx={{
                fontFamily: UI.body,
                fontSize: 12.5,
                fontWeight: lang === key ? 600 : 500,
                border: `1px solid ${lang === key ? UI.gold : UI.line}`,
                bgcolor: lang === key ? UI.goldTint : 'transparent',
                color: lang === key ? UI.gold : UI.ink,
                borderRadius: 1.25,
                px: 1.25,
                height: 28,
                cursor: 'pointer',
              }}
            >
              {label}
              {allIssues(draft, key).length ? ' ⚠' : ''}
            </Box>
          ))}
        </Stack>

        <Typography sx={{ fontSize: 12, color: UI.muted }}>
          <b>Sending now:</b> {versions.sending ? `${versions.sending} (approved)` : 'nothing approved yet'}
          {versions.next && ` · New version ${versions.next}: ${statusWord(versions.nextStatus)}${versions.nextStatus?.status === 'APPROVED' ? ' (took over automatically)' : ''}`}
          {!versions.next && !versions.sending && ` · ${versions.live}: ${statusWord(versions.liveStatus)}`}
        </Typography>
        {(versions.nextStatus?.error || versions.liveStatus?.error) && (
          <Typography sx={{ fontSize: 12, color: '#a12a22' }}>{versions.nextStatus?.error || versions.liveStatus?.error}</Typography>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: UI.ink }}>Message</Typography>
          <Typography sx={{ fontSize: 11.5, fontFamily: UI.mono, color: draft.body[lang].length > BODY_MAX ? '#c62828' : UI.faint }}>
            {draft.body[lang].length}/{BODY_MAX}
          </Typography>
        </Stack>
        <TextField multiline minRows={4} maxRows={12} fullWidth value={draft.body[lang]} onChange={(e) => setBody(e.target.value)} inputRef={bodyRef} inputProps={{ 'aria-label': 'Template message' }} sx={fieldSx} />
        <Stack direction="row" gap={0.5} flexWrap="wrap">
          {draft.variables.map((v, i) => (
            <Tooltip key={v.label} title={`Example: ${v.example}. Click to insert.`}>
              <Box
                component="button"
                type="button"
                onClick={() => insertVariable(i + 1)}
                sx={{ fontFamily: UI.mono, fontSize: 11.5, color: UI.gold, bgcolor: UI.goldTint, border: `1px solid ${UI.gold}55`, borderRadius: 1, px: 0.6, py: 0.1, cursor: 'pointer' }}
              >
                {`{{${i + 1}}}`} {v.label}
              </Box>
            </Tooltip>
          ))}
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: UI.muted }}>
          Variables are optional. When used, keep them sequential and do not start or end with one. Need a new variable? Ask for it to be added first.
        </Typography>

        {draft.footer && (
          <TextField
            size="small"
            label="Footer"
            value={draft.footer[lang]}
            onChange={(e) => setFooter(e.target.value)}
            sx={fieldSx}
            InputProps={{ endAdornment: <Typography sx={{ fontSize: 11, fontFamily: UI.mono, color: draft.footer[lang].length > FOOTER_MAX ? '#c62828' : UI.faint }}>{draft.footer[lang].length}/{FOOTER_MAX}</Typography> }}
          />
        )}
        {draft.buttons?.map((b, i) => (
          <TextField
            key={i}
            size="small"
            label={`Button ${i + 1}`}
            value={b[lang]}
            onChange={(e) => setButton(i, e.target.value)}
            sx={fieldSx}
            helperText={i === 0 && draft.buttons!.length > 1 ? 'Button order is fixed: each position triggers its own action.' : undefined}
            InputProps={{ endAdornment: <Typography sx={{ fontSize: 11, fontFamily: UI.mono, color: b[lang].length > BUTTON_MAX ? '#c62828' : UI.faint }}>{b[lang].length}/{BUTTON_MAX}</Typography> }}
          />
        ))}

        {issues.length > 0 && <Alert severity="warning" sx={{ fontSize: 12.5, py: 0 }}>{issues.join(' ')}</Alert>}
        {error && <Alert severity="error" sx={{ fontSize: 12.5, py: 0 }}>{error}</Alert>}

        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <Button size="small" variant="outlined" disabled={!dirty || !!busy} onClick={save} sx={textButtonSx}>
            {busy === 'save' ? 'Saving…' : 'Save draft'}
          </Button>
          <Button size="small" variant="contained" disableElevation disabled={!!busy || !!api.submitting} onClick={() => submit([lang])} sx={primaryButtonSx}>
            {busy === 'submit' ? 'Submitting…' : `Submit ${lang.toUpperCase()} to Meta`}
          </Button>
          {pendingLangs.length > 1 && (
            <Button size="small" variant="outlined" disabled={!!busy || !!api.submitting} onClick={() => submit(pendingLangs)} sx={textButtonSx}>
              Submit {pendingLangs.map((l) => l.toUpperCase()).join(' + ')}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" disabled={!!busy} onClick={reset} sx={{ ...textButtonSx, color: UI.muted }}>
            {busy === 'reset' ? 'Resetting…' : 'Reset to original'}
          </Button>
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: UI.muted }}>
          An approved template in use is never changed: your edit goes to Meta as a new version and takes over automatically once approved. Until then customers keep getting the approved one.
        </Typography>
      </Stack>

      <Box>
        {draft.header?.type === 'DOCUMENT' && (
          <Typography sx={{ fontSize: 11.5, color: UI.muted, mb: 0.75 }}>
            {draft.header.referenceTemplate === 'swarna_unit_payment_details'
              ? '📄 Unit-specific payment details PDF attached'
              : draft.header.referenceTemplate === 'swarna_signed_booking_form'
                ? '📄 Unit-specific signed booking form PDF attached'
                : '📄 Project brochure PDF attached · website link button below'}
          </Typography>
        )}
        <WhatsappPhonePreview body={templatePreviewText(draft, lang)} footer={draft.footer?.[lang]}
          buttons={(draft.buttons || []).map((b) => b[lang])}
          buttonUrls={draft.urlButton ? [draft.urlButton.sample] : undefined} />
        {draft.urlButton && <Typography sx={{ fontSize: 11.5, color: UI.muted, mt: 0.75 }}>
          Sample link: {draft.urlButton.sample}. {draft.urlButton.note}
        </Typography>}
        <Typography sx={{ fontSize: 11.5, color: UI.faint, mt: 0.75 }}>Preview with sample values{dirty ? ' · unsaved changes' : ''}</Typography>
      </Box>
    </Box>
  );
};

const HubTemplatesTab = ({ templates }: { templates: TemplateStatusesApi }) => {
  const [openName, setOpenName] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [bulk, setBulk] = useState(false);

  const pending = TEMPLATE_DRAFTS.flatMap((d) =>
    AUTO_REPLY_LANGS.filter(({ key }) => templates.needsSubmit(d.name, key)).map(({ key }) => ({ draft: templates.effective(d.name) || d, lang: key }))
  );

  const submitAll = async () => {
    setBulk(true);
    setMessage(null);
    let sent = 0;
    for (const item of pending) {
      const issues = allIssues(item.draft, item.lang);
      if (issues.length) {
        setMessage({ type: 'error', text: `${item.draft.name} (${item.lang}): ${issues.join(' ')}` });
        break;
      }
      // eslint-disable-next-line no-await-in-loop
      const error = await templates.submit(item.draft, item.lang);
      if (error) {
        setMessage({ type: 'error', text: `${item.draft.name} (${item.lang}): ${error}` });
        break;
      }
      sent += 1;
    }
    setBulk(false);
    if (sent === pending.length) setMessage({ type: 'success', text: `Submitted ${sent} template${sent === 1 ? '' : 's'} to Meta for review.` });
  };

  const known = new Set(TEMPLATE_DRAFTS.map((d) => d.name));
  const isVersionOfDraft = (name: string) => known.has(name.replace(/_v\d+$/, ''));
  const otherMeta = templates.metaTemplates.filter((t) => !known.has(t.name) && !isVersionOfDraft(t.name));

  return (
    <Stack gap={2}>
      <Box sx={{ bgcolor: UI.surface, border: `1px solid ${UI.line}`, borderRadius: 2.5 }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${UI.line}` }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: UI.ink }}>Journey templates</Typography>
            <Typography sx={{ fontSize: 12, color: UI.muted }}>
              Click a template to edit its wording in English, Gujarati and Hindi, then submit it to Meta.
            </Typography>
          </Box>
          <Button size="small" variant="outlined" disabled={templates.loading} onClick={templates.refresh} sx={textButtonSx}>
            {templates.loading ? 'Checking…' : 'Sync from Meta'}
          </Button>
          <Button size="small" variant="contained" disableElevation disabled={bulk || !pending.length} onClick={submitAll} sx={primaryButtonSx}>
            {bulk ? 'Submitting…' : pending.length ? `Submit ${pending.length} to Meta` : 'All submitted'}
          </Button>
        </Stack>
        {templates.error && <Alert severity="warning" sx={{ borderRadius: 0, fontSize: 12.5, py: 0 }}>{templates.error}</Alert>}
        {message && (
          <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ borderRadius: 0, fontSize: 12.5, py: 0 }}>
            {message.text}
          </Alert>
        )}

        {TEMPLATE_DRAFTS.map((d, idx) => {
          const open = openName === d.name;
          const automation = JOURNEY_AUTOMATIONS.find((a) => a.key === d.automation);
          const edited = hasEdits(templates.edits[d.name]);
          return (
            <Box key={d.name} sx={{ borderTop: idx ? `1px solid ${UI.line}` : 'none', bgcolor: open ? UI.canvas : 'transparent' }}>
              <Stack
                direction="row"
                alignItems="center"
                gap={1.25}
                flexWrap="wrap"
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={() => setOpenName(open ? null : d.name)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setOpenName(open ? null : d.name);
                }}
                sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { bgcolor: UI.canvas } }}
              >
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Stack direction="row" alignItems="center" gap={0.75}>
                    <Typography sx={{ fontFamily: UI.mono, fontSize: 12.5, color: UI.ink }}>{d.name}</Typography>
                    <CategoryTag category={d.category} />
                    {edited && <Typography sx={{ fontSize: 11, color: UI.gold, fontWeight: 600 }}>edited</Typography>}
                  </Stack>
                  <Typography sx={{ fontSize: 11.5, color: UI.muted }}>
                    {automation ? `${automation.title} · ` : ''}
                    {d.purpose}
                  </Typography>
                </Box>
                <Stack direction="row" gap={0.5}>
                  {AUTO_REPLY_LANGS.map(({ key }) => (
                    <TemplateStatusChip key={key} lang={key} status={templates.statusFor(d.name, key)} />
                  ))}
                </Stack>
              </Stack>
              {open && (
                <TemplateEditor
                  key={d.name}
                  base={d}
                  api={templates}
                  onDone={(text) => setMessage({ type: 'success', text: `${d.name}: ${text}` })}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {otherMeta.length > 0 && (
        <Box sx={{ bgcolor: UI.surface, border: `1px solid ${UI.line}`, borderRadius: 2.5 }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${UI.line}` }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: UI.ink }}>Other templates in Meta</Typography>
            <Typography sx={{ fontSize: 12, color: UI.muted }}>Created elsewhere (campaigns, older automations). Read-only here.</Typography>
          </Box>
          {otherMeta.map((t, idx) => (
            <Stack key={`${t.name}-${t.language}`} direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 0.75, borderTop: idx ? `1px solid ${UI.line}` : 'none' }}>
              <Typography sx={{ fontFamily: UI.mono, fontSize: 12.5, color: UI.ink, flex: 1, minWidth: 0 }}>{t.name}</Typography>
              <CategoryTag category={t.category} />
              <TemplateStatusChip lang={t.language} status={{ status: t.status }} />
            </Stack>
          ))}
        </Box>
      )}
    </Stack>
  );
};

export default HubTemplatesTab;
